import { useEffect, useState, type ReactNode } from 'react';

import { AgentFrontmatter } from './AgentFrontmatter';

import type { AgentDetailData } from './AgentsView';


type Tab = 'details' | 'code';

interface Props {
  agentId: string;
  detail: AgentDetailData | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string;
  canManage: boolean;
  onClose: () => void;
  onDeactivate: (id: string) => Promise<void> | void;
  /** Optional code-and-revisions slot rendered when the user opens that tab. */
  codeTab?: ReactNode;
}

export function AgentDetail({
  agentId,
  detail,
  status,
  error,
  canManage,
  onClose,
  onDeactivate,
  codeTab,
}: Props) {
  const [tab, setTab] = useState<Tab>('details');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    setTab('details');
  }, [agentId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-label="Agent details"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.32)',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 120ms ease',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: 'min(640px, 100%)',
          maxWidth: '100%',
          height: '100%',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--border-strong)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          transform: mounted ? 'translateX(0)' : 'translateX(16px)',
          opacity: mounted ? 1 : 0,
          transition: 'transform 180ms ease, opacity 180ms ease',
        }}
      >
        <Header
          name={detail?.name}
          slug={detail?.slug}
          status={detail?.status}
          onClose={onClose}
        />
        <TabBar tab={tab} onSelect={setTab} />

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {status === 'loading' && !detail && <Centered>Loading agent&hellip;</Centered>}
          {status === 'error' && (
            <Centered error>{error ?? 'Failed to load this agent.'}</Centered>
          )}

          {detail && tab === 'details' && (
            <DetailsTab detail={detail} canManage={canManage} onDeactivate={onDeactivate} />
          )}
          {detail && tab === 'code' && (codeTab ?? <CodeAndRevisionsPlaceholder />)}
        </div>
      </div>
    </div>
  );
}

function Header({
  name,
  slug,
  status,
  onClose,
}: {
  name?: string;
  slug?: string;
  status?: 'active' | 'deactivated';
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        flexShrink: 0,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: status === 'deactivated' ? 'var(--text-soft)' : 'var(--kind-success)',
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: HEADING_SERIF,
            fontSize: 16,
            fontWeight: 500,
            color: 'var(--text)',
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {name ?? ' '}
        </div>
        {slug && (
          <div
            style={{
              fontFamily: MONO,
              fontSize: 10.5,
              color: 'var(--text-soft)',
              letterSpacing: '0.02em',
            }}
          >
            @{slug}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 5,
          padding: '4px 9px',
          color: 'var(--text-mute)',
          fontSize: 11,
          fontFamily: 'inherit',
          cursor: 'pointer',
          letterSpacing: '0.02em',
        }}
      >
        Close
      </button>
    </div>
  );
}

function TabBar({ tab, onSelect }: { tab: Tab; onSelect: (t: Tab) => void }) {
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        flexShrink: 0,
      }}
    >
      <TabButton active={tab === 'details'} onClick={() => onSelect('details')}>
        Agent Details
      </TabButton>
      <TabButton active={tab === 'code'} onClick={() => onSelect('code')}>
        Code &amp; Revisions
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '10px 16px',
        fontSize: 12,
        fontWeight: 600,
        color: active ? 'var(--accent)' : 'var(--text-mute)',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
        cursor: 'pointer',
        fontFamily: 'inherit',
        letterSpacing: '0.01em',
      }}
    >
      {children}
    </button>
  );
}

function DetailsTab({
  detail,
  canManage,
  onDeactivate,
}: {
  detail: AgentDetailData;
  canManage: boolean;
  onDeactivate: (id: string) => Promise<void> | void;
}) {
  const capabilities = Object.entries(detail.role_capabilities).filter(([, v]) => v === true);
  const deactivated = detail.status === 'deactivated';

  return (
    <div>
      <AgentFrontmatter
        name={detail.name}
        description={detail.description}
        frontmatter={detail.frontmatter}
        hasSoulMd={detail.has_soul_md}
      />

      <Section title="Role">
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontFamily: HEADING_SERIF,
              fontSize: 16,
              fontWeight: 500,
              color: 'var(--text)',
            }}
          >
            {detail.role.name}
          </div>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 10.5,
              color: 'var(--text-soft)',
              letterSpacing: '0.04em',
            }}
          >
            {detail.role.slug}
          </div>
        </div>
      </Section>

      <Section title={`Capabilities · ${capabilities.length.toString().padStart(2, '0')}`}>
        {capabilities.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
            No capabilities granted by this role.
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '6px 12px',
            }}
          >
            {capabilities.map(([k]) => (
              <li
                key={k}
                style={{
                  fontFamily: MONO,
                  fontSize: 11.5,
                  color: 'var(--text)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: 'var(--accent)',
                    flexShrink: 0,
                  }}
                />
                {k}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={`Skills granted · ${detail.granted_skills.length.toString().padStart(2, '0')}`}>
        {detail.granted_skills.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
            This role grants no skills.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {detail.granted_skills.map((s) => (
              <span
                key={s.id}
                style={{
                  fontSize: 11.5,
                  padding: '4px 9px',
                  background: 'var(--bg-alt)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  color: 'var(--text)',
                  letterSpacing: '0.01em',
                }}
              >
                {s.name}
              </span>
            ))}
          </div>
        )}
      </Section>

      <Section title="Provenance">
        <dl
          style={{
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 16,
            rowGap: 6,
            fontSize: 11.5,
          }}
        >
          <Meta k="created_by" v={detail.created_by_email} />
          <Meta k="created_at" v={detail.created_at} />
          <Meta k="updated_at" v={detail.updated_at} />
        </dl>
      </Section>

      {canManage && !deactivated && (
        <Section title="Danger zone">
          <button
            type="button"
            onClick={() => onDeactivate(detail.id)}
            style={{
              padding: '7px 13px',
              fontSize: 12,
              fontFamily: 'inherit',
              fontWeight: 500,
              color: 'var(--kind-failure)',
              background: 'transparent',
              border: '1px solid var(--kind-failure)',
              borderRadius: 5,
              cursor: 'pointer',
              letterSpacing: '0.01em',
            }}
          >
            Deactivate agent
          </button>
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: 'var(--text-soft)',
              lineHeight: 1.5,
              maxWidth: 480,
            }}
          >
            Deactivation stops the agent from accepting new work. AGENT.md and SOUL.md stay in git
            for audit; revisions remain visible.
          </div>
        </Section>
      )}
      {canManage && deactivated && (
        <Section title="Status">
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
            This agent is currently <strong style={{ color: 'var(--text-mute)' }}>deactivated</strong>.
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        padding: '18px 24px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontFamily: MONO,
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: 'var(--text-soft)',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function Meta({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt
        style={{
          fontFamily: MONO,
          fontSize: 10.5,
          color: 'var(--text-soft)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        {k}
      </dt>
      <dd
        style={{
          margin: 0,
          fontFamily: MONO,
          fontSize: 11.5,
          color: 'var(--text-mute)',
          wordBreak: 'break-word',
        }}
      >
        {v}
      </dd>
    </>
  );
}

function Centered({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: error ? 'var(--kind-failure)' : 'var(--text-mute)',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

// DESIGN_DEVIATION: deliberate placeholder. The brief instructs us to leave a slot here for the
// existing Skills file-tree component to be reused later. No Skills-side parallel yet wired in.
export function CodeAndRevisionsPlaceholder() {
  return (
    <div
      style={{
        padding: '40px 24px',
        textAlign: 'center',
        color: 'var(--text-soft)',
      }}
    >
      <div
        aria-hidden
        style={{
          width: 40,
          height: 40,
          margin: '0 auto 14px',
          borderRadius: 4,
          background: 'var(--bg-alt2, var(--bg-alt))',
          border: '1px dashed var(--border-strong)',
        }}
      />
      <div
        style={{
          fontFamily: HEADING_SERIF,
          fontSize: 16,
          fontWeight: 500,
          color: 'var(--text-mute)',
          letterSpacing: '-0.01em',
        }}
      >
        File tree &amp; git revisions
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 12,
          color: 'var(--text-soft)',
          maxWidth: 320,
          margin: '6px auto 0',
          lineHeight: 1.5,
        }}
      >
        Reuses the Skills file browser. Coming in the next iteration.
      </div>
    </div>
  );
}

const HEADING_SERIF =
  "ui-serif, Georgia, 'Iowan Old Style', 'Apple Garamond', Palatino, serif";
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
